import { useMemo, useState } from 'react';
import * as XLSX from 'xlsx';
import {
  Box, Stack, Typography, Chip, Button, Alert, Collapse, Divider, Accordion, AccordionSummary, AccordionDetails,
  Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions,
} from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import DownloadIcon from '@mui/icons-material/Download';
import PublishIcon from '@mui/icons-material/Publish';
import ReplayIcon from '@mui/icons-material/Replay';
import EditIcon from '@mui/icons-material/Edit';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useDispatch, useSelector } from 'react-redux';
import { useSnackbar } from 'notistack';
import {
  markZipReady, setUploadResults, replaceUploadResult, markFix, setCell, setBlendCell,
  nextStage, prevStage, setStage,
} from '../../../../redux/tatWizardSlice';
import {
  stNameFromGroups, ZONES,
  buildCsvBody, computeDeviations, filterPricedCodes, findStGroup,
} from '../constants';
import { useGetServiceabilityGroupsQuery } from '../../../../redux/commonApiCalls';
import { useUploadTatCsvMutation, useMarkTatLiveMutation } from '../../../../redux/tatApiSlice';
import TatCellInput from '../components/TatCellInput';

function MetricCard({ label, value, sub, color = 'text.primary', bg = 'background.paper', borderColor = 'divider' }) {
  return (
    <Box sx={{ flex: 1, minWidth: 150, bgcolor: bg, border: 1, borderColor, borderRadius: 2, p: 1.5 }}>
      <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: 10.5, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
        {label}
      </Typography>
      <Typography variant="h5" sx={{ fontSize: 22, fontWeight: 800, mt: 0.25, color, lineHeight: 1.1 }}>{value}</Typography>
      {sub && <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: 11 }}>{sub}</Typography>}
    </Box>
  );
}

function downloadText(name, content, mime = 'text/plain;charset=utf-8') {
  const blob = new Blob([content], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}


function ErrorRowCard({ row, standardTat }) {
  const dispatch = useDispatch();
  const [open, setOpen] = useState(false);
  const tat = useSelector((s) => s.tatWizard.tat);
  const tatBlended = useSelector((s) => s.tatWizard.tatBlended);
  const sourceMap = useSelector((s) => s.tatWizard.source);
  const { data: groups = [] } = useGetServiceabilityGroupsQuery();
  const mode = row.mode || 'blended';
  const st = row.service_type;
  const parent = row.parent;
  const data = mode === 'blended' ? (tatBlended[st] || {}) : (tat[st]?.[parent] || {});
  const std = standardTat?.[st] || null;
  const stName = stNameFromGroups(st, groups);

  return (
    <Box sx={{ border: 1, borderColor: 'error.light', borderRadius: 1, mb: 1.25, overflow: 'hidden' }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ px: 1.5, py: 1, bgcolor: 'error.lighter' }}>
        <Chip
          icon={<ErrorOutlineIcon sx={{ fontSize: 14 }} />}
          size="small" color="error" label={`HTTP ${row.code}`}
          sx={{ height: 22, fontWeight: 700 }}
        />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontFamily: 'JetBrains Mono, ui-monospace, monospace', fontSize: 11.5, fontWeight: 700 }}>
            {row.accountCode}
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', fontSize: 11 }}>
            {row.parent} · {stName} · {row.error_mode}
          </Typography>
          <Typography variant="caption" sx={{ color: 'error.dark', display: 'block', fontSize: 11.5 }}>
            {row.message}
          </Typography>
        </Box>
        <Button
          size="small" startIcon={<EditIcon sx={{ fontSize: 14 }} />}
          variant={open ? 'contained' : 'outlined'} color="primary"
          onClick={() => setOpen((v) => !v)}
          sx={{ fontSize: 11 }}
        >
          {open ? 'Close' : 'Edit'}
        </Button>
      </Stack>
      <Collapse in={open}>
        <Box sx={{ p: 1.5, bgcolor: 'background.paper' }}>
          <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mb: 1 }}>
            Editing TAT for{' '}
            {mode === 'blended'
              ? <Box component="b">all account codes under {stName}</Box>
              : <Box component="b">{parent}</Box>}
            . Same red/green highlight as the main editor (vs standard). After fix, click{' '}
            <b>Re-publish failures only</b>.
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: `repeat(${ZONES.length}, minmax(120px, 1fr))`, gap: 1 }}>
            {ZONES.map((z) => {
              const stdMin = std?.[z.id]?.[0];
              const stdMax = std?.[z.id]?.[1];
              return (
                <Box key={z.id} sx={{ bgcolor: 'grey.50', border: 1, borderColor: 'divider', borderRadius: 1, p: 0.75 }}>
                  <Typography sx={{ fontWeight: 700, fontSize: 11 }}>{z.name}</Typography>
                  <Stack direction="row" spacing={0.5} sx={{ mt: 0.5 }}>
                    <TatCellInput
                      value={data[`z${z.id}_min`]}
                      std={stdMin}
                      source={sourceMap[`${st}__${parent}__z${z.id}_min`] || 'manual'}
                      onChange={(v) => {
                        if (mode === 'blended') {
                          dispatch(setBlendCell({ st, z: z.id, bound: 'min', value: v }));
                        } else {
                          dispatch(setCell({ st, parent, z: z.id, bound: 'min', value: v }));
                        }
                        dispatch(markFix(row.accountCode));
                      }}
                    />
                    <TatCellInput
                      value={data[`z${z.id}_max`]}
                      std={stdMax}
                      source={sourceMap[`${st}__${parent}__z${z.id}_max`] || 'manual'}
                      onChange={(v) => {
                        if (mode === 'blended') {
                          dispatch(setBlendCell({ st, z: z.id, bound: 'max', value: v }));
                        } else {
                          dispatch(setCell({ st, parent, z: z.id, bound: 'max', value: v }));
                        }
                        dispatch(markFix(row.accountCode));
                      }}
                    />
                  </Stack>
                  <Typography variant="caption" sx={{ display: 'block', mt: 0.4, color: 'text.secondary', fontSize: 10 }}>
                    Std: {stdMin ?? '–'}–{stdMax ?? '–'}d
                  </Typography>
                </Box>
              );
            })}
          </Box>
        </Box>
      </Collapse>
    </Box>
  );
}

export default function Stage4Submit() {
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const tat = useSelector((s) => s.tatWizard.tat);
  const tatBlended = useSelector((s) => s.tatWizard.tatBlended);
  const modeByST = useSelector((s) => s.tatWizard.modeByST);
  const uploadResults = useSelector((s) => s.tatWizard.uploadResults);
  const zipReady = useSelector((s) => s.tatWizard.zipReady);
  const fixes = useSelector((s) => s.tatWizard.fixes);
  const merchantName = useSelector((s) => s.tatWizard.merchantName);
  const merchantOid = useSelector((s) => s.tatWizard.merchantOid);
  const selectedSTs = useSelector((s) => s.tatWizard.selectedSTs);
  const standardTat = useSelector((s) => s.tatWizard.standardTat);
  const pricedKeys = useSelector((s) => s.tatWizard.pricedKeys || []);
  const tatOriginal = useSelector((s) => s.tatWizard.tatOriginal);
  const { data: groups = [] } = useGetServiceabilityGroupsQuery();

  const files = useMemo(() => {
    const result = [];
    (selectedSTs || []).forEach((st) => {
      const group = findStGroup(groups, st);
      // Only generate TAT files for the couriers the merchant has pricing for
      // (matches by prozo_id, AC code or parent). Empty pricedKeys = not loaded → all.
      const linkedCodes = filterPricedCodes(
        (group?.linkedCodes || []).filter((lc) => lc.isActive !== false),
        pricedKeys,
      );
      const mode = modeByST[st] || 'courier_wise';
      if (mode === 'blended') {
        const payload = tatBlended[st] || {};
        result.push({ name: `MERCHANT_TAT.merchant.${merchantOid}.st.${st}.blended.csv`, content: buildCsvBody(payload), accountCode: '', parent: '', service_type: st, mode: 'blended' });
      } else {
        const parents = [...new Set(linkedCodes.map((lc) => lc.parent || lc.cpAccountCode))];
        parents.forEach((parent) => {
          const codes = linkedCodes.filter((lc) => (lc.parent || lc.cpAccountCode) === parent);
          const payload = tat[st]?.[parent] || {};
          const csv = buildCsvBody(payload);
          console.log('Built CSV for', csv);
          codes.forEach((lc) => result.push({ name: `MERCHANT_TAT.merchant.${merchantOid}.courier.${lc.cpAccountCode}.csv`, content: csv, accountCode: lc.cpAccountCode, parent, service_type: st, mode: 'courier_wise' }));
        });
      }
    });
    return result;
  }, [tat, tatBlended, modeByST, merchantOid, selectedSTs, groups, pricedKeys]);

  const summary = useMemo(
    () => computeDeviations(tat, tatBlended, modeByST, selectedSTs, standardTat, groups),
    [tat, tatBlended, modeByST, selectedSTs, standardTat, groups],
  );

  const blendedSTs = (selectedSTs || []).filter((st) => (modeByST[st] || 'courier_wise') === 'blended').length;
  const cpwiseSTs = (selectedSTs || []).length - blendedSTs;

  const ok = uploadResults.filter((r) => r.status === 'ok').length;
  const err = uploadResults.filter((r) => r.status === 'error').length;
  const ranYet = uploadResults.length > 0;
  // 6th analytics card flips to a filled good/bad state once a publish has run.
  const statusLabel = !ranYet ? 'Status' : err > 0 ? 'Errors' : 'Uploaded';
  const statusValue = !ranYet ? 'Not pushed yet' : err > 0 ? err : ok;
  const statusSub = !ranYet ? undefined : `of ${files.length}`;
  const statusColor = !ranYet ? 'text.primary' : err > 0 ? 'error.main' : 'success.main';
  const statusBg = !ranYet ? 'background.paper' : err > 0 ? 'error.lighter' : 'success.lighter';
  const statusBorder = !ranYet ? 'divider' : err > 0 ? 'error.light' : 'success.light';
  const fullySucceeded = ranYet && err === 0;

  const [generating, setGenerating] = useState(false);
  const [publishing, setPublishing] = useState(false);
  // "TATs are being changed" guard: when editing a merchant that already has
  // live TAT (tatOriginal snapshot present), warn before the publish overwrites it.
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [uploadTatCsv] = useUploadTatCsvMutation();
  const [markTatLive] = useMarkTatLiveMutation();

  // Compute, per service-type, whether the current TAT differs from what was
  // loaded from the existing config. null tatOriginal = fresh setup → no warning.
  const changedSTs = useMemo(() => {
    if (!tatOriginal) return [];
    const changed = [];
    (selectedSTs || []).forEach((st) => {
      const mode = modeByST[st] || 'courier_wise';
      const curr = mode === 'blended' ? (tatBlended[st] || {}) : (tat[st] || {});
      const orig = mode === 'blended' ? (tatOriginal.tatBlended?.[st] || {}) : (tatOriginal.tat?.[st] || {});
      if (JSON.stringify(curr) !== JSON.stringify(orig)) changed.push(st);
    });
    return changed;
  }, [tatOriginal, selectedSTs, modeByST, tat, tatBlended]);

  // Build a single Excel workbook with one sheet per courier account code.
  const onGenerate = () => {
    setGenerating(true);
    try {
      const wb = XLSX.utils.book_new();
      const ZONE_NAMES = ['Local', 'Within State', 'Metro', 'Rest of India', 'Special'];

      files.forEach((f) => {
        // Parse CSV rows back into zone × min/max
        const csvLines = f.content.split('\n').slice(1); // skip header
        const rows = csvLines.map((line) => {
          const cols = line.replace(/"/g, '').split(',');
          return { zone: cols[0], min: cols[1], max: cols[2] };
        });

        // Sheet data: info block + zone table
        const sheetData = [
          ['Service Type', f.service_type],
          ['Parent Courier', f.parent || '—'],
          ['Account Code', f.accountCode || '(Blended)'],
          ['Mode', f.mode === 'blended' ? 'Blended' : 'Courier-Wise'],
          [],
          ['Zone', 'Zone Name', 'Min TAT (days)', 'Max TAT (days)'],
          ...rows.map((r, i) => [
            Number(r.zone),
            ZONE_NAMES[i] || `Zone ${r.zone}`,
            r.min !== '' ? Number(r.min) : '',
            r.max !== '' ? Number(r.max) : '',
          ]),
        ];

        const ws = XLSX.utils.aoa_to_sheet(sheetData);
        // Style column widths
        ws['!cols'] = [{ wch: 6 }, { wch: 18 }, { wch: 16 }, { wch: 16 }];

        // Sheet name: accountCode (max 31 chars, Excel limit)
        const sheetName = (f.accountCode || `${f.service_type}_blended`).slice(0, 31);
        XLSX.utils.book_append_sheet(wb, ws, sheetName);
      });

      XLSX.writeFile(wb, `MERCHANT_TAT_${merchantName}.xlsx`);
      dispatch(markZipReady(true));
      enqueueSnackbar(`Excel downloaded · ${files.length} courier tab(s)`, { variant: 'success' });
    } catch {
      enqueueSnackbar('Failed to build the Excel file — please retry', { variant: 'error' });
    } finally {
      setGenerating(false);
    }
  };

  // Returns true when every zone row in the CSV has non-empty min and max values.
  const isCsvComplete = (f) => {
    const lines = f.content.split('\n').slice(1); // skip header
    if (lines.length < 5) return false;
    return lines.every((line) => {
      const cols = line.split(',');
      return cols[1] !== '' && cols[1] !== undefined && cols[2] !== '' && cols[2] !== undefined;
    });
  };

  // Sequential upload: one POST per file to real API 6
  const onPublish = async () => {
    // Pre-flight: warn about files with missing TAT values and skip them
    const incomplete = files.filter((f) => !isCsvComplete(f));
    if (incomplete.length > 0) {
      enqueueSnackbar(
        `${incomplete.length} file(s) skipped — TAT values missing for: ${incomplete.map((f) => f.accountCode || f.service_type).join(', ')}`,
        { variant: 'warning' },
      );
    }
    const filesToUpload = files.filter((f) => isCsvComplete(f));
    if (!filesToUpload.length) {
      enqueueSnackbar('No files have complete TAT values — fill in all 5 zones before publishing.', { variant: 'error' });
      return;
    }
    setPublishing(true);
    dispatch(setUploadResults([]));
    const results = [];
    for (const f of filesToUpload) {
      // Prefer user-picked CSV file (raw bytes, like --data-binary @file); fall back to built string.
      const res = await uploadTatCsv({
        merchantOid,
        serviceType: f.service_type,
        accountCode: f.mode === 'blended' ? '' : f.accountCode,
        pricingMode: f.mode,
        csvBody: f.content,
        fileRecord: f,
      });
      const result = res.data || {
        status: 'error', code: 500, error_mode: 'server_error',
        message: 'Upload failed', accountCode: f.accountCode,
        parent: f.parent, service_type: f.service_type, mode: f.mode,
      };
      results.push({ filename: f.name, ...result });
    }
    dispatch(setUploadResults(results));
    const errCount = results.filter((r) => r.status === 'error').length;
    if (errCount === 0) {
      enqueueSnackbar('All files published successfully', { variant: 'success' });
      // Mark TAT live on the server (API 7)
      try {
        await markTatLive({
          merchantOid,
          publishedSts: selectedSTs,
          fileCount: filesToUpload.length,
        });
      } catch { /* live-status update failure is non-blocking */ }
    } else {
      enqueueSnackbar(`${errCount} file(s) failed — inline-fix and re-publish`, { variant: 'warning' });
    }
    setPublishing(false);
  };

  // Re-upload only the failed files after inline fixes
  const onRerunFailures = async () => {
    setPublishing(true);
    const errored = uploadResults.filter((r) => r.status === 'error' && fixes[r.accountCode]);
    for (const r of errored) {
      const f = files.find((x) => x.accountCode === r.accountCode && x.service_type === r.service_type);
      if (!f) continue;
      const res = await uploadTatCsv({
        merchantOid,
        serviceType: f.service_type,
        accountCode: f.mode === 'blended' ? '' : f.accountCode,
        pricingMode: f.mode,
        csvBody: f.content,
        fileRecord: f,
      });
      dispatch(replaceUploadResult({ filename: f.name, ...(res.data || { status: 'error', accountCode: f.accountCode }) }));
    }
    const stillErr = uploadResults.filter((r) => r.status === 'error' && !fixes[r.accountCode]).length;
    if (stillErr === 0) enqueueSnackbar('All failures recovered', { variant: 'success' });
    else enqueueSnackbar(`${stillErr} row(s) still need fixing`, { variant: 'warning' });
    setPublishing(false);
  };

  const onExportSummary = () => {
    const head = '# Upload Summary - ' + new Date().toISOString();
    const meta = `# Merchant: ${merchantName} (${merchantOid})`;
    const successLines = uploadResults.filter((r) => r.status === 'ok')
      .map((r) => `"${r.filename}","${r.accountCode}","${r.parent}","${r.service_type}","${r.mode}","${r.code}","${r.message}"`);
    const failLines = uploadResults.filter((r) => r.status === 'error')
      .map((r) => `"${r.filename}","${r.accountCode}","${r.parent}","${r.service_type}","${r.mode}","${r.code}","${r.error_mode || ''}","${r.message}"`);
    const stamp = new Date().toISOString().slice(0, 10);
    const csv = [
      head, meta, '',
      '## SUCCESS',
      'filename,account_code,parent,service_type,mode,http_code,message',
      ...successLines, '',
      '## FAILURE',
      'filename,account_code,parent,service_type,mode,http_code,error_mode,message',
      ...failLines,
    ].join('\n');
    downloadText(`TAT_upload_summary_${merchantName}_${stamp}.csv`, csv, 'text/csv;charset=utf-8');
  };

  return (
    <Stack spacing={2}>
      <Stack direction="row" spacing={1.5} flexWrap="wrap" rowGap={1.5}>
        <MetricCard label="Files to push" value={files.length} sub="One per account code" />
        <MetricCard label="Service types" value={selectedSTs.length} sub={`${blendedSTs} blended · ${cpwiseSTs} CP-wise`} />
        <MetricCard label="Higher than std" value={summary.higher} sub={`${((summary.higher / Math.max(summary.total, 1)) * 100).toFixed(1)}%`} color="error.main" />
        <MetricCard label="Lower than std" value={summary.lower} sub={`${((summary.lower / Math.max(summary.total, 1)) * 100).toFixed(1)}%`} color="success.main" />
        <MetricCard label="Zero values" value={summary.zeros} sub={`${((summary.zeros / Math.max(summary.total, 1)) * 100).toFixed(1)}%`} color="warning.main" />
        <MetricCard
          label={statusLabel}
          value={statusValue}
          sub={statusSub}
          color={statusColor}
          bg={statusBg}
          borderColor={statusBorder}
        />
      </Stack>

      {/* Pre-publish actions. Generate/Publish drop away once everything is live
          so they don't compete with the in-results Finish primary. */}
      <Stack direction="row" spacing={1.25} flexWrap="wrap" rowGap={1} alignItems="center">
        <Button
          variant="outlined" color="inherit" startIcon={<ArrowBackIcon />}
          onClick={() => dispatch(prevStage())}
        >
          Back to Review
        </Button>
        {!fullySucceeded && (
          <>
            <Button
              variant="outlined" startIcon={<DownloadIcon />}
              disabled={generating}
              onClick={onGenerate}
            >
              {generating ? 'Building Excel…' : 'Generate & Download Excel'}
            </Button>
            <Button
              variant="contained" color="primary"
              startIcon={<PublishIcon />}
              disabled={!zipReady || publishing}
              onClick={() => (changedSTs.length > 0 ? setConfirmOpen(true) : onPublish())}
            >
              {publishing ? 'Publishing…' : 'Publish to ProShip'}
            </Button>
          </>
        )}
      </Stack>

      <Alert severity="info" variant="outlined" sx={{ fontSize: 12.5 }}>
        <b>{files.length} TAT file{files.length !== 1 ? 's' : ''}</b> ready to publish —{' '}
        {files.length} courier account code{files.length !== 1 ? 's' : ''} across {selectedSTs?.length || 0} service type{(selectedSTs?.length || 0) !== 1 ? 's' : ''}.
        Blended service types share one TAT set across all couriers; courier-wise service types use per-courier values.
        Click <b>Generate & Download ZIP</b> to save a local copy, then <b>Publish to ProShip</b> to upload.
      </Alert>

      {uploadResults.length > 0 && (
        <Box>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
            {err === 0
              ? <CheckCircleIcon sx={{ color: 'success.main' }} />
              : <ErrorOutlineIcon sx={{ color: 'error.main' }} />}
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
              {err === 0
                ? `All ${files.length} files published`
                : `${err} of ${files.length} files failed`}
            </Typography>
          </Stack>
          <Stack direction="row" spacing={1.5} flexWrap="wrap" rowGap={1.5} sx={{ mb: 1.75 }}>
            <MetricCard label="Files" value={uploadResults.length} />
            <MetricCard label="Published" value={ok} color="success.main" />
            <MetricCard label="Errors" value={err} color={err > 0 ? 'error.main' : 'text.primary'} />
            <MetricCard
              label="Merchant OID"
              value={(
                <Box component="span" sx={{ fontFamily: 'JetBrains Mono, ui-monospace, monospace', fontSize: 13 }}>
                  {`${merchantOid.slice(0, 12)}…`}
                </Box>
              )}
            />
          </Stack>
          {/* Post-publish actions live with the results recap, mirroring the reference
              results-zone so the pre-publish row stays uncluttered. */}
          <Stack direction="row" spacing={1.25} flexWrap="wrap" rowGap={1} alignItems="center" sx={{ mb: 1.75 }}>
            {err > 0 && (
              <Button variant="contained" color="error" startIcon={<ReplayIcon />} onClick={onRerunFailures}>
                Re-publish failures only ({err})
              </Button>
            )}
            <Button variant="outlined" startIcon={<DownloadIcon />} onClick={onExportSummary}>
              Download upload summary
            </Button>
            <Button variant="text" color="inherit" startIcon={<EditIcon />} onClick={() => dispatch(setStage(2))}>
              Back to bulk edit
            </Button>
            {err === 0 && (
              <Button variant="contained" color="success" endIcon={<ArrowForwardIcon />} onClick={() => dispatch(nextStage())}>
                Finish
              </Button>
            )}
          </Stack>
          {uploadResults.filter((r) => r.status === 'error').map((r) => (
            <ErrorRowCard key={r.accountCode} row={r} standardTat={standardTat} />
          ))}
          {ok > 0 && (
            <Accordion sx={{ '&:before': { display: 'none' }, border: 1, borderColor: 'divider', borderRadius: 1, mt: 1 }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <CheckCircleIcon sx={{ color: 'success.main', fontSize: 18 }} />
                  <Typography sx={{ fontWeight: 700, fontSize: 13 }}>{ok} successful uploads</Typography>
                </Stack>
              </AccordionSummary>
              <AccordionDetails>
                <Stack spacing={0.5}>
                  {uploadResults.filter((r) => r.status === 'ok').map((r) => (
                    <Stack key={r.accountCode} direction="row" spacing={1} alignItems="center" sx={{ fontSize: 11.5 }}>
                      <Chip size="small" color="success" label="OK" sx={{ height: 18, fontSize: 9.5 }} />
                      <Typography variant="caption" sx={{ fontFamily: 'JetBrains Mono, ui-monospace, monospace', fontSize: 11 }}>
                        {r.accountCode}
                      </Typography>
                      <Typography variant="caption" sx={{ color: 'text.secondary' }}>· {r.parent} · {r.service_type}</Typography>
                    </Stack>
                  ))}
                </Stack>
              </AccordionDetails>
            </Accordion>
          )}
        </Box>
      )}

      {/* "TATs are being changed" guard — only shown when editing a merchant
          whose live TAT differs from what's about to be published. */}
      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <WarningAmberIcon color="warning" />
          TATs are being changed
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ fontSize: 13.5 }}>
            You're about to overwrite the live TAT for{' '}
            <b>{changedSTs.length} service type{changedSTs.length !== 1 ? 's' : ''}</b>.
            This replaces the values currently in effect for this merchant.
          </DialogContentText>
          {changedSTs.length > 0 && (
            <Stack direction="row" spacing={0.75} flexWrap="wrap" rowGap={0.75} sx={{ mt: 1.5 }}>
              {changedSTs.map((st) => (
                <Chip key={st} size="small" color="warning" variant="outlined" label={stNameFromGroups(st, groups)} />
              ))}
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button color="inherit" onClick={() => setConfirmOpen(false)}>Cancel</Button>
          <Button
            variant="contained" color="warning" startIcon={<PublishIcon />}
            onClick={() => { setConfirmOpen(false); onPublish(); }}
          >
            Yes, change &amp; publish
          </Button>
        </DialogActions>
      </Dialog>

    </Stack>
  );
}
